import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  timeout: 120000,
  withCredentials: true,
});

export const api = {
  health: () => client.get("/").then((r) => r.data),
  // Auth
  register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
  login: (payload) => client.post("/auth/login", payload).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  // My Incidents (logged-in)
  myIncidents: () => client.get("/me/incidents").then((r) => r.data),
  // Admin
  adminMetrics: () => client.get("/admin/metrics").then((r) => r.data),
  adminAnalytics: (range = "7d") =>
    client.get(`/admin/analytics?range=${range}`).then((r) => r.data),
  adminUsers: () => client.get("/admin/users").then((r) => r.data),
  adminActivity: (limit = 50) =>
    client.get(`/admin/activity?limit=${limit}`).then((r) => r.data),
  adminIncidents: (status, limit = 100) =>
    client
      .get("/admin/incidents", { params: { status, limit } })
      .then((r) => r.data),
  adminIncidentFull: (id) =>
    client.get(`/admin/incidents/${id}/full`).then((r) => r.data),
  adminUpdateStatus: (id, status, reason = "") =>
    client
      .patch(`/admin/incidents/${id}/status`, { status, reason })
      .then((r) => r.data),
  // Existing
  listSamples: () => client.get("/samples").then((r) => r.data),
  listIncidents: () => client.get("/incidents").then((r) => r.data),
  getIncident: (id) => client.get(`/incidents/${id}`).then((r) => r.data),
  createIncident: (payload) =>
    client.post("/incidents", payload).then((r) => r.data),
  createFromSample: (key) =>
    client.post(`/incidents/from-sample/${key}`).then((r) => r.data),
  // Admin one-off plan restore (Pro only) — part of subscription persistence hardening
  adminRestorePlan: (email, reason) =>
    client
      .post("/admin/plan/restore", { email, reason })
      .then((r) => r.data),
  analyze: (id, weights) =>
    client
      .post(`/incidents/${id}/analyze`, weights || null)
      .then((r) => r.data),
  approve: (id, payload) =>
    client.post(`/incidents/${id}/approve`, payload).then((r) => r.data),
  deleteIncident: (id) =>
    client.delete(`/incidents/${id}`).then((r) => r.data),
  archive: (id) =>
    client.post(`/incidents/${id}/archive`).then((r) => r.data),
  unarchive: (id) =>
    client.post(`/incidents/${id}/unarchive`).then((r) => r.data),
  listIncidentsAll: () =>
    client.get(`/incidents?include_archived=true`).then((r) => r.data),
  stats: () => client.get("/stats").then((r) => r.data),
  timeseries: (days = 30) =>
    client.get(`/stats/timeseries?days=${days}`).then((r) => r.data),
  getBaselines: () => client.get("/settings/baselines").then((r) => r.data),
  updateBaselines: (payload) =>
    client.put("/settings/baselines", payload).then((r) => r.data),
  mttrReport: (id) =>
    client.get(`/incidents/${id}/mttr-report`).then((r) => r.data),
  mttrPdfUrl: (id) => `${API}/incidents/${id}/mttr-report.pdf`,
  mttrArchivedUrl: (id) => `${API}/incidents/${id}/mttr-report.archived`,
  fullReportPdfUrl: (id) => `${API}/incidents/${id}/full-report.pdf`,
  fullReportJsonUrl: (id) => `${API}/incidents/${id}/full-report.json`,
  submitFeedback: (id, payload) =>
    client.post(`/incidents/${id}/feedback`, payload).then((r) => r.data),
  ackRunbookStep: (id, stepId, payload) =>
    client
      .post(`/incidents/${id}/runbook/steps/${stepId}/ack`, payload)
      .then((r) => r.data),
  shareEnable: (id) =>
    client.post(`/incidents/${id}/share`).then((r) => r.data),
  shareRevoke: (id) =>
    client.delete(`/incidents/${id}/share`).then((r) => r.data),
  publicByToken: (token) =>
    client.get(`/public/incidents/by-token/${token}`).then((r) => r.data),
  // Password reset
  requestPasswordReset: (email) =>
    client
      .post("/auth/password-reset/request", { email })
      .then((r) => r.data),
  confirmPasswordReset: (token, newPassword) =>
    client
      .post("/auth/password-reset/confirm", {
        token,
        new_password: newPassword,
      })
      .then((r) => r.data),
  // Billing
  billingPlan: () => client.get("/billing/plan").then((r) => r.data),
  billingSetCountry: (country) =>
    client.post("/billing/country", { country }).then((r) => r.data),
  billingCheckout: (country, originUrl) =>
    client
      .post("/billing/checkout", { country, origin_url: originUrl })
      .then((r) => r.data),
  billingCheckoutStatus: (sessionId) =>
    client
      .get(`/billing/checkout/status/${sessionId}`)
      .then((r) => r.data),
  billingPortal: (returnUrl) =>
    client.post("/billing/portal", { return_url: returnUrl }).then((r) => r.data),
  // Admin plan management
  adminSetUserPlan: (userId, payload) =>
    client.patch(`/admin/users/${userId}/plan`, payload).then((r) => r.data),
  adminUserUsage: (userId, limit = 100) =>
    client
      .get(`/admin/users/${userId}/usage`, { params: { limit } })
      .then((r) => r.data),
};

export default api;
